import React, { Component } from "react";
import { Box, Button, CircularProgress } from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";

import { organizationReportService } from "_services/organizationReport.service";
import { ReportBody, injectTailwindCdn } from "views/organizationReport/index";
// Active facility = URL ?facilityId= (Org Admin new-tab) -> else own DP facility.
import { getActiveFacility } from "__helpers/activeFacility";

// Single-facility version of the Executive Dashboard: KPIs, funnel, activity-by-day,
// speed-to-finalize, plus the facility's can-accept backlog & critical alerts.
// Excluded: hero/$-ROI impact banner, multi-facility network table, MaxAlly section.
const FACILITY_SECTIONS = ["alerts", "kpis", "funnel", "activity", "backlog", "speed", "maxally"];

export default class FacilityReport extends Component {
  constructor(props) {
    super(props);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.facility = getActiveFacility() || {};
    this.state = {
      startDate: thirtyDaysAgo,
      endDate: today,
      loading: false,
      data: null,
      error: null,
      hasGenerated: false,
    };
  }

  componentDidMount() {
    injectTailwindCdn();
  }

  handleGenerate = async () => {
    const { startDate, endDate } = this.state;
    if (!startDate || !endDate || !this.facility) return;
    this.setState({ loading: true, error: null, hasGenerated: true });
    try {
      const data = await organizationReportService.fetchFacilityReport({
        facilityId: this.facility.facilityId,
        startDate,
        endDate,
      });
      this.setState({ data, loading: false });
    } catch (e) {
      this.setState({ error: e.message, loading: false, data: null });
    }
  };

  render() {
    const { startDate, endDate, loading, data, hasGenerated, error } = this.state;

    if (!this.facility) {
      return <div style={{ padding: 20 }}>No facility context available for this user.</div>;
    }

    // Real single-facility data only — no demo fallback. Zeros mean no activity in the window.
    const kpis = (data && data.networkKpis) || {};
    const funnel = (data && data.funnel) || {};
    const activity = (data && data.activityByDay) || [];
    const backlog = (data && data.canAcceptBacklog) || [];
    const speed = (data && data.speedDistribution) || {};
    const alerts = (data && data.criticalAlerts) || [];
    const maxAlly = (data && data.maxAlly) || {};
    const noActivity = hasGenerated && data && !kpis.totalSends && !kpis.uniqueReferrals;

    return (
      <div className="orgreport-root">
        <GridContainer>
          <GridItem xs={12}>
            <Card>
              <CardBody>
                <Box display="flex" flexWrap="wrap" gridGap={16} alignItems="center" mb={2}>
                  <Box style={{ minWidth: 200, fontWeight: 600, fontSize: "1rem" }}>
                    {this.facility.facilityName}
                  </Box>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      label="Start Date"
                      format="MM/dd/yyyy"
                      value={startDate}
                      onChange={(d) => this.setState({ startDate: d })}
                      maxDate={endDate || new Date()}
                    />
                    <KeyboardDatePicker
                      label="End Date"
                      format="MM/dd/yyyy"
                      value={endDate}
                      onChange={(d) => this.setState({ endDate: d })}
                      minDate={startDate}
                      maxDate={new Date()}
                    />
                  </MuiPickersUtilsProvider>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleGenerate}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : "Generate Report"}
                  </Button>
                </Box>

                {error ? (
                  <Box mb={2} style={{ color: "#dc2626", fontSize: "0.85rem" }}>
                    {error}
                  </Box>
                ) : null}

                {!hasGenerated ? (
                  <Box py={6} textAlign="center" style={{ color: "#6b6969", fontSize: "0.9rem" }}>
                    Select a date range, then click <strong>Generate Report</strong> to view your
                    facility report.
                  </Box>
                ) : loading ? (
                  /* Wait for the API before rendering any report elements — avoids the flash of
                     undefined/zero values while the request is in flight. */
                  <Box
                    py={8}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    style={{ color: "#6b6969" }}
                  >
                    <CircularProgress />
                    <Box mt={2} style={{ fontSize: "0.9rem" }}>
                      Generating report…
                    </Box>
                  </Box>
                ) : data ? (
                  <div
                    className="orgreport-body"
                    style={{ maxHeight: "78vh", overflowY: "auto", overflowX: "hidden" }}
                  >
                    {noActivity ? (
                      <Box mb={2} style={{ color: "#7b7979", fontSize: "0.85rem" }}>
                        No referral activity for <strong>{this.facility.facilityName}</strong> in the
                        selected date range.
                      </Box>
                    ) : null}
                    <ReportBody
                      organization={{ name: this.facility.facilityName }}
                      window={{ startDate, endDate }}
                      kpis={kpis}
                      funnel={funnel}
                      activity={activity}
                      backlog={backlog}
                      facilities={[]}
                      speed={speed}
                      alerts={alerts}
                      maxAlly={maxAlly}
                      sections={FACILITY_SECTIONS}
                      footer={false}
                    />
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}
